import "./style/Coaches.css";
import { useEffect, useMemo, useState } from "react";
import debounce from "lodash.debounce";
import { isAuthenticated } from "../apicalls/auth";
import {
  approvePackageRefundRequest,
  getPackagePurchases,
  getPackageRefundRequests,
  rejectPackageRefundRequest,
  updatePackagePurchaseExpiry,
} from "../apicalls/packages";
import Paginations from "./Paginations";
import {
  defaultExpiryInput,
  endOfDayIso,
  formatPackageExpiry,
  isPackageExpired,
} from "./packageExpiryUtils";

const normalizeRows = (data) => {
  if (Array.isArray(data?.purchases)) {
    return data.purchases;
  }
  if (Array.isArray(data?.data)) {
    return data.data;
  }
  if (Array.isArray(data?.results)) {
    return data.results;
  }
  if (Array.isArray(data)) {
    return data;
  }
  return [];
};

const normalizeTotal = (data, fallback) => {
  if (typeof data?.total === "number") {
    return data.total;
  }
  if (typeof data?.count === "number") {
    return data.count;
  }
  if (typeof data?.pagination?.total === "number") {
    return data.pagination.total;
  }
  if (typeof data?.meta?.total === "number") {
    return data.meta.total;
  }
  return fallback;
};

const mapRefundRequestsByPurchase = (requests = []) =>
  requests.reduce((map, request) => {
    const purchaseId = String(request.purchase_id);
    if (!map[purchaseId]) {
      map[purchaseId] = request;
    }
    return map;
  }, {});

const refundApprovalStarted = (request) =>
  Boolean(request?.stripe_refund_id || request?.metadata?.approval_started_at);

const formatCellValue = (value) => {
  if (value === null || value === undefined) {
    return "-";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
};

const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleString();
};

const formatPaid = (row) => {
  if (row.status === "refunded") {
    return "Refunded";
  }
  if (row.status === "failed") {
    return "Failed";
  }
  if (row.paid === false || row.status === "reserved") {
    return "Unpaid";
  }
  if (row.paid === true || row.status === "active" || row.status === "succeeded") {
    return "Paid";
  }
  return "-";
};

const getPaymentState = (row) => {
  if (row.status === "refunded") {
    return {
      label: "Refunded - credits revoked",
      className: "package-status-badge package-status-unknown",
    };
  }
  if (row.status === "failed") {
    return {
      label: "Payment failed - no credits",
      className: "package-status-badge package-status-unknown",
    };
  }
  if (row.status === "reserved" || row.paid === false) {
    return {
      label: "Legacy reservation - unpaid",
      className: "package-status-badge package-status-reserved",
    };
  }
  if (isPackageExpired(row)) {
    return {
      label: "Paid - expired",
      className: "package-status-badge package-status-expired",
    };
  }
  if (row.paid === true || row.status === "active" || row.status === "succeeded") {
    return {
      label: "Paid - credits active",
      className: "package-status-badge package-status-paid",
    };
  }
  return {
    label: formatCellValue(row.status),
    className: "package-status-badge package-status-unknown",
  };
};

const columns = [
  { key: "id", label: "ID" },
  { key: "player_full_name", label: "Player Name" },
  { key: "player_email", label: "Player Email" },
  { key: "coach_full_name", label: "Coach Name" },
  { key: "coach_email", label: "Coach Email" },
  { key: "package_name", label: "Package Name" },
  { key: "package_description", label: "Package Description" },
  { key: "package_price", label: "Package Price" },
  { key: "credits_remaining", label: "Credits Remaining" },
  { key: "expires_at", label: "Expires" },
  { key: "status", label: "Status" },
  { key: "paid", label: "Paid" },
  { key: "payment_state", label: "Payment State" },
  { key: "reserved_at", label: "Reserved At" },
  { key: "charged_payment_intent_id", label: "Charged PI" },
  { key: "charged_at", label: "Charged At" },
  { key: "created_at", label: "Created At" },
  { key: "updated_at", label: "Updated At" },
  { key: "refund_status", label: "Refund Request" },
  { key: "refund_actions", label: "Refund Actions" },
];

function PackagePurchases() {
  const [purchases, setPurchases] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [coachId, setCoachId] = useState("");
  const [playerId, setPlayerId] = useState("");
  const [status, setStatus] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [refundRequests, setRefundRequests] = useState({});
  const [refundActionId, setRefundActionId] = useState("");
  const [refundMessage, setRefundMessage] = useState("");
  // The purchase whose expiry is being changed, and the date picked for it.
  const [expiryEdit, setExpiryEdit] = useState(null);
  const [expirySaving, setExpirySaving] = useState(false);

  const { access_token } = isAuthenticated();

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    getPackagePurchases(access_token, {
      coach_id: coachId || undefined,
      player_id: playerId || undefined,
      status: status || undefined,
      search: search || undefined,
      perPage,
      page,
    }).then((data) => {
      if (!isMounted) {
        return;
      }
      const rows = normalizeRows(data);
      setPurchases(rows);
      setTotal(normalizeTotal(data, rows.length));
      setLoading(false);

      const purchaseIds = rows.map((purchase) => purchase.id).filter(Boolean);
      if (!purchaseIds.length) {
        setRefundRequests({});
        return;
      }
      getPackageRefundRequests(access_token, {
        purchase_ids: purchaseIds.join(","),
        perPage: 250,
      }).then((data) => {
        if (!isMounted) return;
        setRefundRequests(mapRefundRequestsByPurchase(data.requests));
      }).catch((err) => {
        if (!isMounted) return;
        setRefundMessage(err.message || "Failed to load refund requests.");
      });
    });

    return () => {
      isMounted = false;
    };
  }, [access_token, coachId, playerId, status, search, perPage, page]);

  const deBounceOnChange = useMemo(
    () =>
      debounce((value) => {
        setSearch(value);
        setPage(1);
      }, 400),
    []
  );

  useEffect(() => () => deBounceOnChange.cancel(), [deBounceOnChange]);

  const handlePerPageChange = (event) => {
    setPerPage(Number(event.target.value));
    setPage(1);
  };

  const handleFilterChange = (setter) => (event) => {
    setter(event.target.value);
    setPage(1);
  };

  const paginate = (number) => {
    setPage(number);
  };

  const refreshPurchases = async () => {
    const data = await getPackagePurchases(access_token, {
      coach_id: coachId || undefined,
      player_id: playerId || undefined,
      status: status || undefined,
      search: search || undefined,
      perPage,
      page,
    });
    const rows = normalizeRows(data);
    setPurchases(rows);
    setTotal(normalizeTotal(data, rows.length));
    return rows;
  };

  const refreshRefundRequests = async (rows) => {
    const purchaseIds = rows.map((purchase) => purchase.id).filter(Boolean);
    if (!purchaseIds.length) {
      setRefundRequests({});
      return;
    }
    const data = await getPackageRefundRequests(access_token, {
      purchase_ids: purchaseIds.join(","),
      perPage: 250,
    });
    setRefundRequests(mapRefundRequestsByPurchase(data.requests));
  };

  const refreshPageData = async () => {
    const rows = await refreshPurchases();
    await refreshRefundRequests(rows);
  };

  const approveRefund = async (requestId) => {
    if (!window.confirm("Approve Stripe refund for this untouched paid package?")) return;
    setRefundActionId(String(requestId));
    setRefundMessage("");
    try {
      try {
        const result = await approvePackageRefundRequest(access_token, requestId);
        setRefundMessage(
          result.request?.status === "approved"
            ? "Refund approved."
            : result.request?.status === "failed"
              ? "Stripe refund failed. The request is marked failed."
              : `Stripe refund ${result.refund?.status || "is not complete"}; request remains pending.`
        );
      } catch (err) {
        setRefundMessage(err.message || "Failed to approve refund.");
        try {
          await refreshPageData();
        } catch {
          setRefundMessage(`${err.message || "Failed to approve refund."} Refresh package data before taking another action.`);
        }
        return;
      }

      try {
        await refreshPageData();
      } catch (err) {
        setRefundMessage("Refund action completed, but package data could not be refreshed.");
      }
    } finally {
      setRefundActionId("");
    }
  };

  const rejectRefund = async (requestId) => {
    const note = window.prompt("Reason for rejection", "");
    if (note === null) return;
    setRefundActionId(String(requestId));
    setRefundMessage("");
    try {
      try {
        await rejectPackageRefundRequest(access_token, requestId, note);
        setRefundMessage("Refund rejected.");
      } catch (err) {
        setRefundMessage(err.message || "Failed to reject refund.");
        return;
      }

      try {
        await refreshPageData();
      } catch (err) {
        setRefundMessage("Refund rejected. Package data could not be refreshed.");
      }
    } finally {
      setRefundActionId("");
    }
  };

  const saveExpiry = async (purchaseId, expiresAt) => {
    setExpirySaving(true);
    setRefundMessage("");
    try {
      await updatePackagePurchaseExpiry(access_token, purchaseId, expiresAt);
      setExpiryEdit(null);
      setRefundMessage(
        expiresAt
          ? `Package #${purchaseId} now expires ${formatPackageExpiry(expiresAt)}.`
          : `Package #${purchaseId} no longer expires.`
      );
      await refreshPurchases();
    } catch (err) {
      setRefundMessage(err.message || "Failed to change the expiry.");
    } finally {
      setExpirySaving(false);
    }
  };

  const renderExpiryCell = (row) => {
    const editing = expiryEdit && String(expiryEdit.id) === String(row.id);
    if (editing) {
      const expiresAt = endOfDayIso(expiryEdit.date);
      return (
        <div className="expiry-edit">
          <input
            type="date"
            value={expiryEdit.date}
            onChange={(event) => setExpiryEdit({ id: row.id, date: event.target.value })}
          />
          <button type="button" onClick={() => saveExpiry(row.id, expiresAt)} disabled={!expiresAt || expirySaving}>
            {expirySaving ? "Saving..." : "Save"}
          </button>
          <button type="button" onClick={() => saveExpiry(row.id, null)} disabled={expirySaving}>
            No expiry
          </button>
          <button type="button" onClick={() => setExpiryEdit(null)} disabled={expirySaving}>
            Cancel
          </button>
        </div>
      );
    }
    const expired = isPackageExpired(row);
    const changeable = !["refunded", "failed"].includes(row.status);
    return (
      <div className="expiry-cell">
        <span>{formatPackageExpiry(row.expires_at)}</span>
        {expired ? <span className="package-status-badge package-status-expired">Expired</span> : null}
        {changeable ? (
          <button type="button" onClick={() => setExpiryEdit({ id: row.id, date: defaultExpiryInput(row) })}>
            {expired ? "Extend" : "Change"}
          </button>
        ) : null}
      </div>
    );
  };

  return (
    <main>
      <div className="coaches_title">
        <h1>Package Purchases</h1>
      </div>
      <div className="filter">
        <div>
          <select name="perPage" value={perPage} onChange={handlePerPageChange}>
            {[10, 20, 50, 100].map((val) => (
              <option key={val} value={val}>
                {val}
              </option>
            ))}
          </select>
          <span>
            show {perPage} out of {total}
          </span>
        </div>
        <input
          className="w-25"
          type="text"
          onChange={(event) => deBounceOnChange(event.target.value)}
          placeholder="Search"
        />
      </div>
      <div className="filter filter-inline">
        <input
          type="text"
          value={coachId}
          onChange={handleFilterChange(setCoachId)}
          placeholder="Coach ID"
          className="ledger-filter-input"
        />
        <input
          type="text"
          value={playerId}
          onChange={handleFilterChange(setPlayerId)}
          placeholder="Player ID"
          className="ledger-filter-input"
        />
        <input
          type="text"
          value={status}
          onChange={handleFilterChange(setStatus)}
          placeholder="Status"
          className="ledger-filter-input"
        />
      </div>
      <div className="col-11">
        {refundMessage ? <div className="small">{refundMessage}</div> : null}
        {loading ? (
          <div>Loading package purchases...</div>
        ) : purchases.length ? (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th scope="col" key={column.key}>
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {purchases.map((row, index) => (
                  <tr key={row.id ?? index}>
                    {columns.map((column) => {
                      const paymentState = getPaymentState(row);
                      const refundRequest = refundRequests[String(row.id)];
                      return (
                        <td key={`${row.id ?? index}-${column.key}`}>
                          {column.key === "refund_status" ? (
                            refundRequest ? formatCellValue(refundRequest.status) : "-"
                          ) : column.key === "refund_actions" ? (
                            refundRequest?.status === "pending" ? (
                              <div className="refund-actions">
                                <button type="button" onClick={() => approveRefund(refundRequest.id)} disabled={refundActionId === String(refundRequest.id)}>
                                  {refundActionId === String(refundRequest.id) ? "Working..." : "Approve"}
                                </button>
                                {refundApprovalStarted(refundRequest) ? null : (
                                  <button type="button" onClick={() => rejectRefund(refundRequest.id)} disabled={refundActionId === String(refundRequest.id)}>
                                    Reject
                                  </button>
                                )}
                              </div>
                            ) : "-"
                          ) : column.key === "expires_at" ? (
                            renderExpiryCell(row)
                          ) : column.key === "payment_state" ? (
                            <span className={paymentState.className}>
                              {paymentState.label}
                            </span>
                          ) : column.key === "paid" ? (
                            formatPaid(row)
                          ) : column.key === "charged_at" ? (
                            formatDateTime(row.metadata?.charged_at)
                          ) : column.key === "created_at" ||
                            column.key === "updated_at" ||
                            column.key === "reserved_at" ? (
                            formatDateTime(row[column.key])
                          ) : (
                            formatCellValue(row[column.key])
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div>
            <h2>Package purchases not available</h2>
          </div>
        )}
        {total > perPage ? (
          <Paginations
            perPage={perPage}
            total={total}
            paginate={paginate}
            currentPage={page}
          />
        ) : null}
      </div>
    </main>
  );
}

export default PackagePurchases;
